import React, { useEffect, useRef, useState } from 'react';
import { Download, RefreshCw, X } from 'lucide-react';
import { APP_VERSION, APP_VERSION_CODE } from '../version';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 5 * 60 * 1000; // every 5 min
const SUPER_ADMIN_BYPASS_KEY = 'carloo_super_admin_bypass';

const isNativeApp = () => {
  try {
    const cap = window.Capacitor;
    return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
  } catch { return false; }
};

// Super-admin escape-hatch. Without this a misconfigured `app_version`
// record in the DB can lock the only person able to fix it out of the
// admin panel. We hit /api/auth/is-super-admin once on mount; if the
// answer is yes, the mandatory blocker becomes dismissible. The result
// is cached in sessionStorage so the second & subsequent renders don't
// have to re-hit the API.
async function checkSuperAdminBypass() {
  try {
    const cached = sessionStorage.getItem(SUPER_ADMIN_BYPASS_KEY);
    if (cached === '1') return true;
    if (cached === '0') return false;
    const token = localStorage.getItem('token');
    if (!token) return false;
    const r = await fetch(`${API_URL}/api/auth/is-super-admin`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return false;
    const d = await r.json();
    const ok = !!d.is_super_admin;
    sessionStorage.setItem(SUPER_ADMIN_BYPASS_KEY, ok ? '1' : '0');
    return ok;
  } catch { return false; }
}

/**
 * AppVersionGuard — WEB-ONLY companion to <AppUpdateChecker /> (native).
 *
 * Native (APK / iOS) users get the existing `AppUpdateChecker` which reads
 * the installed `versionCode` from @capacitor/app and offers the APK URL.
 *
 * Web users (team.carloo.in opened in a browser) instead compare the
 * **bundled** `APP_VERSION_CODE` (baked into the build at compile time)
 * against the server's latest `version_code`. When stale:
 *   • Non-mandatory  → dismissible bottom-right banner.
 *   • Mandatory      → full-screen blocker.
 *
 * "Update Now" performs a cache + service-worker purge and a hard reload
 * so the browser fetches the fresh JS/CSS bundle. No Play Store / APK
 * download needed.
 *
 * Renders nothing when running natively or when no update is available.
 */
export default function AppVersionGuard() {
  const [latest, setLatest] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const timer = useRef(null);

  const fetchLatest = async () => {
    try {
      const r = await fetch(`${API_URL}/api/app-version`, { cache: 'no-store' });
      if (!r.ok) return;
      const d = await r.json();
      setLatest(d);
    } catch (_) {
      // silent — next poll retries
    }
  };

  useEffect(() => {
    if (isNativeApp()) return; // native handled by AppUpdateChecker
    fetchLatest();
    checkSuperAdminBypass().then(setIsSuperAdmin);
    timer.current = setInterval(fetchLatest, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchLatest(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (isNativeApp()) return null;
  if (!latest) return null;
  const serverCode = parseInt(latest.version_code || 0, 10);
  const updateAvailable = serverCode > APP_VERSION_CODE;
  if (!updateAvailable) return null;
  // Soft banner is always dismissible. For mandatory updates we additionally
  // honour a per-tab "Continue as super-admin" dismissal so an out-of-sync
  // version_code record can be repaired from Admin → Masters → App Update.
  if (dismissed && (!latest.mandatory || isSuperAdmin)) return null;

  const doUpdate = async () => {
    setBusy(true);
    try {
      // Blow away SW + caches so the new build is picked up on reload.
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k)));
        }
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        }
      } catch {}
      window.location.reload(true);
    } finally {
      setBusy(false);
    }
  };

  // Mandatory update → full-screen blocker.
  if (latest.mandatory) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="version-blocker">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-100 rounded-full"><Download className="text-red-600" size={22} /></div>
            <div>
              <h3 className="font-bold text-lg text-gray-900">Update Required</h3>
              <p className="text-xs text-gray-500">You're on {APP_VERSION} — server is on {latest.version_name}</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 mt-3">
            This version is no longer supported. Please reload to install the latest update.
          </p>
          {latest.changelog && (
            <pre className="mt-3 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap max-h-40 overflow-auto" data-testid="version-changelog">{latest.changelog}</pre>
          )}
          <button
            onClick={doUpdate}
            disabled={busy}
            className="mt-5 w-full px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
            data-testid="version-update-btn"
          >
            {busy ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Reload Now
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setDismissed(true)}
              className="mt-2 w-full px-4 py-2 text-xs text-gray-600 hover:text-gray-900 underline"
              data-testid="version-superadmin-bypass-btn"
              title="Bypass this update once so you can fix the version_code in Admin → Masters"
            >
              Continue as Super Admin (one-time bypass)
            </button>
          )}
        </div>
      </div>
    );
  }

  // Optional update → bottom-right banner (dismissible).
  return (
    <div className="fixed bottom-4 right-4 z-[9990] max-w-sm bg-white border border-amber-200 shadow-xl rounded-xl p-4" data-testid="version-banner">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-100 rounded-full flex-shrink-0"><Download className="text-amber-700" size={18} /></div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900">Update available</p>
          <p className="text-xs text-gray-600 mt-0.5">
            You're on <span className="font-mono">{APP_VERSION}</span> — latest is <span className="font-mono">{latest.version_name}</span>.
          </p>
          {latest.changelog && (
            <pre className="mt-2 text-[11px] text-gray-600 bg-gray-50 rounded p-2 whitespace-pre-wrap max-h-24 overflow-auto">{latest.changelog}</pre>
          )}
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={doUpdate}
              disabled={busy}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white text-xs font-semibold rounded-lg"
              data-testid="version-update-btn"
            >
              {busy ? 'Updating…' : 'Reload'}
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-800"
              data-testid="version-dismiss-btn"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-gray-400 hover:text-gray-700"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
